import { applyOperation, JsonPatchError } from 'fast-json-patch';
import cloneDeep from 'lodash/cloneDeep';

// Recursively check if a value is deeply empty (all leaves are null, undefined,
// empty string, empty array, or empty object). Aidbox rejects an empty value
// ("The property's value cannot be empty"), so a patch op carrying one is never sent.
export const isEmptyPatchValue = (value) => {
    if (value === null || value === undefined || value === '') return true;
    if (Array.isArray(value)) return value.length === 0 || value.every((item) => isEmptyPatchValue(item));
    if (typeof value === 'object') {
        const keys = Object.keys(value);
        return keys.length === 0 || keys.every((key) => isEmptyPatchValue(value[key]));
    }
    return false;
};

// Drops `add`/`replace` ops whose value is missing or deeply empty.
// - 'add' with a deeply-empty value is dropped (the field may not exist yet,
//   so a 'remove' would fail with "Nothing to remove").
// - 'replace' with a deeply-empty value is dropped (Aidbox rejects these with
//   "empty-value" validation errors).
// Whoever advances a saved baseline must advance it by the ops that survive this,
// not by the ops that were computed, or the baseline claims values the server never got (#6256).
export const dropEmptyValueOps = (ops) => ops.filter((op) => !(typeof op === 'object' && op !== null && (op.op === 'add' || op.op === 'replace') && (!('value' in op) || isEmptyPatchValue(op.value))));

// Aidbox's 422 diagnostics for a JSON patch that does not fit the server's copy of the resource:
// the patch was computed against a baseline that has drifted from what the server holds.
const DRIFT_DIAGNOSTICS = /No parent object or array @|Nothing to remove @|Value to replace does not present @/;

export const isPatchDriftError = (error) => error?.response?.status === 422 && (error.response.data?.issue || []).some((issue) => DRIFT_DIAGNOSTICS.test(issue?.diagnostics || ''));

const unescapeSegment = (segment) => segment.replace(/~1/g, '/').replace(/~0/g, '~');
const escapeSegment = (segment) => String(segment).replace(/~/g, '~0').replace(/\//g, '~1');
const valueAt = (json, segments) => segments.reduce((node, key) => (node !== null && typeof node === 'object' ? node[key] : undefined), json);

// The shortest prefix of `segments` that `doc` can take as a whole: the first key `doc` is missing,
// or the array (or primitive) that cannot hold the rest of the path.
const missingPrefix = (doc, segments) => {
    let node = doc;
    for (let i = 0; i < segments.length; i++) {
        const key = segments[i];
        if (node === null || typeof node !== 'object') return segments.slice(0, i);
        if (Array.isArray(node)) {
            if (!/^\d+$/.test(key) || Number(key) >= node.length) return segments.slice(0, i);
        } else if (!Object.prototype.hasOwnProperty.call(node, key)) {
            return segments.slice(0, i + 1);
        }
        node = node[key];
    }
    return segments;
};

/**
 * Applies `ops` to a copy of `baseJson`, tolerating the drift that makes Aidbox reject a patch.
 * `ops` were computed against a copy that may no longer match `baseJson`, toward `targetJson`:
 * - an op that applies cleanly is applied as is
 * - a `remove` of a path `baseJson` lacks is skipped; it is already gone
 * - an `add`/`replace` whose parent (or array index) `baseJson` lacks writes the missing subtree
 *   whole, taken from `targetJson`
 * Only the paths the ops touch are changed, so anything else `baseJson` holds is left as it is.
 */
export const rebaseJsonPatch = (baseJson, ops, targetJson) => {
    const doc = cloneDeep(baseJson ?? {});

    ops.forEach((op) => {
        try {
            applyOperation(doc, cloneDeep(op), true, true);
            return;
        } catch (error) {
            if (!(error instanceof JsonPatchError)) throw error;
        }

        if (op.op !== 'add' && op.op !== 'replace') return;

        const prefix = missingPrefix(doc, op.path.split('/').slice(1).map(unescapeSegment));
        const value = valueAt(targetJson, prefix);
        if (!prefix.length || value === undefined) return;
        applyOperation(doc, { op: 'add', path: prefix.map((segment) => '/' + escapeSegment(segment)).join(''), value: cloneDeep(value) }, false, true);
    });

    return doc;
};
